<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue';

import {
  usePerformanceStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import {
  HotelDetailsPageComponentAsyncImport,
  TripOverviewPageComponentAsyncImport,
} from '@apps/main/modules/router/tripPlannerRoute';
import type { LatLongPoint } from '@apps/main/new-desktop/modules/newMaps';

import { performanceMark, useBackgroundComponentsPreload } from '@libs/utils';

import type { HotelSelectionCardData } from '../models';
import { useHotelSelectionStore, useHotelSelectionListStore } from '../stores';

import HotelSelectionDynamicScroller from './HotelSelectionDynamicScroller.vue';
import HotelSelectionNoResults from './HotelSelectionNoResults.vue';

interface HotelSelectionListProps {
  data: HotelSelectionCardData[];
  shouldDisplayLoader: boolean;
  isMobile?: boolean;
}

const props = defineProps<HotelSelectionListProps>();

interface HotelSelectionListEmits {
  (
    eventName: 'hightlight-on-map',
    data: { hotelCode: string | undefined; position: LatLongPoint | undefined }
  ): void;
  (eventName: 'initialized-hotel-selection-list'): void;
}

const emit = defineEmits<HotelSelectionListEmits>();

const hotelSelectionStore = useHotelSelectionStore();
const hotelSelectionListStore = useHotelSelectionListStore();
const performanceStore = usePerformanceStore();
const statsigEventsStore = useStatsigEventsStore();

const preloadComponents = useBackgroundComponentsPreload([
  TripOverviewPageComponentAsyncImport,
  HotelDetailsPageComponentAsyncImport,
]);

onMounted(() => {
  if (hotelSelectionListStore.isFirstHotelSelectionListRender) {
    if (props.data.length) {
      performanceMark('HotelSelectionListRendered');
    }
    hotelSelectionListStore.isFirstHotelSelectionListRender = false;
  }

  if (props.data.length) {
    performanceStore.trackHotelSelectionPageRenderedWithContentPerformance();

    preloadComponents.start();
  }
});

onUnmounted(() => preloadComponents.stop());

function onResetFilters(): void {
  hotelSelectionStore.resetAllFilters();
  statsigEventsStore.logHotelSelectionNoResultsResetClicked();
}
</script>

<template>
  <div class="hotel-selection-list">
    <HotelSelectionDynamicScroller
      v-if="props.data.length || props.shouldDisplayLoader"
      :data="props.data"
      :is-mobile="props.isMobile"
      :should-display-loader="props.shouldDisplayLoader"
      @hightlight-on-map="emit('hightlight-on-map', $event)"
      @initialized-hotel-selection-list="
        emit('initialized-hotel-selection-list')
      "
    />
    <HotelSelectionNoResults
      v-else
      :has-active-hotel-filters="hotelSelectionStore.hasActiveHotelFilters"
      @reset-filters="onResetFilters"
    />
  </div>
</template>

<style scoped lang="scss">
.hotel-selection-list {
  display: flex;
  flex-direction: column;
  height: 100%;
}
</style>
